import manager from '@mailobj-browser/front/js/contracts/manager.js'
import preventDefault
  from '@mailobj-browser/front/js/events/hooks/preventDefault.js'
import stopImmediatePropagation
  from '@mailobj-browser/front/js/events/hooks/stopImmediatePropagation.js'
import {
  tapDown,
  tapUp
} from '@mailobj-browser/front/js/events/listeners/builtins/tap.js'
import listener from '@mailobj-browser/front/js/events/listeners/listener.js'
import capture from '@mailobj-browser/front/js/events/options/capture.js'
import passive from '@mailobj-browser/front/js/events/options/passive.js'
import input from '@mailobj-browser/front/js/events/types/input.js'
import keyUp from '@mailobj-browser/front/js/events/types/keyUp.js'
import all from '@mailobj-browser/front/js/selectors/all.js'
import closest from '@mailobj-browser/front/js/selectors/closest.js'
import one from '@mailobj-browser/front/js/selectors/one.js'
import append from '@mailobj-browser/front/js/tree/append.js'
import element from '@mailobj-browser/front/js/tree/element.js'
import prepend from '@mailobj-browser/front/js/tree/prepend.js'
import remove from '@mailobj-browser/front/js/tree/remove.js'
import removed from '@mailobj-browser/front/js/tree/removed.js'
import array from '@mailobj-browser/front/js/utils/array.js'
import object from '@mailobj-browser/front/js/utils/object.js'
import wait from '@mailobj-browser/front/js/utils/wait.js'
import * as hotkey from '../../hotkey/hotkey.js'
import * as utilities from '../../styles.js'
import * as generics from '#components/generics/js/styles.js'
import dropdown, { close, open, opener } from '../menus/dropdown.js'

const lists = new WeakMap()
const templates = new WeakMap()
const values = new WeakMap()
const bubbling = { bubbles: true }

let picked = false
let chars = ''
let timeout = null

const cleanup = () => {
  chars = ''
  timeout = null
}

const select = async (list, input) => {
  const select = opener(list)
  const { ownerDocument, parentNode } = select
  const { defaultView } = ownerDocument
  const { InputEvent } = defaultView
  const { dataset, parentNode: inputParent, value, name } = input
  const { textContent } = inputParent
  const { src } = one('img', inputParent) ?? {}
  const current = one('option', select)
  const option = element(ownerDocument, 'option', { value }, textContent)
  const iconSelected = one(`.${utilities.modifiers.icon.selected}`, closest('ol,ul', input))
  const { content } = one('template', select)
  const label = closest('label', input)
  const { parentNode: item } = label
  const placeholder = one(`.${utilities.elements.placeholder}`, item)
  const itemIcon = one(`.${utilities.elements.icon}`, label)
  const selectIcon = one(`.${utilities.elements.icon}:not(.${utilities.elements.dropdown_icon})`, parentNode)

  if (selectIcon?.classList.contains(utilities.elements.icon_option)) {
    remove(selectIcon)
  }

  if (itemIcon && !itemIcon.classList.contains(utilities.modifiers.icon.selected)) {
    const iconClone = itemIcon.cloneNode(true)

    iconClone.classList.remove(generics.elements.button_icon)
    iconClone.classList.add(generics.elements.new_field_select_group_icon)
    await manager.fragment(iconClone)
    prepend(parentNode, iconClone)
  }

  if (iconSelected) {
    const iconContent = one(`.${utilities.modifiers.icon.selected}`, content)

    prepend(closest('label', one(`[name='${name}'][value='${value}']`, content)), iconContent)
    prepend(label, iconSelected)
  }

  if (current) {
    remove(current)
  }

  for (const node of item.childNodes) {
    if (node !== label) {
      append(option, node)
    }
  }

  if (placeholder) {
    select.classList.add(`${utilities.elements.placeholder}`)
  } else {
    select.classList.remove(`${utilities.elements.placeholder}`)
  }

  Object.assign(option.dataset, { ...dataset })
  append(select, option)
  select.selectedIndex = 0

  if (!picked) {
    input.focus()
  }

  input.checked = true
  picked = false

  if (values.get(select) !== value) {
    values.set(select, value)
    select.dispatchEvent(new InputEvent('input', bubbling))
  }

  return select
}

const task = async (select, event) => {
  if (lists.has(select)) {
    close()

    return
  }

  const { key } = event

  if (['Escape', 'Tab'].includes(key)) {
    return
  }

  preventDefault(event)

  const { ownerDocument } = select
  const container = one(`.${utilities.elements.aside_lightboxes}`, ownerDocument)
  const list = await open(templates.get(select), container, select)

  hotkey.disable(list)
  await dropdown(list)
  onListInput.listen(list)
  onListKeyUp.listen(list)
  onListTapUp.listen(list)
  lists.set(select, list)

  queueMicrotask(async () => {
    await removed(list)
    cleanup()
    lists.delete(select)
  })
}

const onSelectKeyUp = object(listener, {
  type: keyUp,
  task
})

const onSelectTapDown = object(tapDown, {
  hooks: array([
    preventDefault
  ]),
  task: () => {}
})

const onSelectTapUp = object(tapUp, {
  capture,
  task
})

const onListInput = object(listener, {
  type: input,
  passive,
  task: async (list, event) => {
    const { target } = event
    const item = closest('li', target)

    if (!item) {
      return
    }

    const input = one('input', item)

    await select(list, input)
  }
})

const onListKeyUp = object(listener, {
  type: keyUp,
  task: async (list, event) => {
    const { key, target } = event

    if (['Tab', 'Enter'].includes(key)) {
      preventDefault(event)
      picked = true
      target.click()
      opener(list)?.focus()

      return
    }

    if (key.length !== 1) {
      return
    }

    const char = key.toLowerCase()
    const inputs = all('input[value]:not([disabled])', list)

    chars += char

    if (chars.replace(char, '').length) {
      for (const input of inputs) {
        const { parentNode } = input
        const { textContent } = parentNode
        const text = textContent.trim().toLowerCase()

        if (text.startsWith(chars)) {
          preventDefault(event)
          clearTimeout(timeout)
          await select(list, input)
          timeout = setTimeout(cleanup, 1000)

          return
        }
      }

      return
    }

    let first
    let current
    let next

    for (const input of inputs) {
      const { checked, parentNode } = input
      const { textContent } = parentNode
      const text = textContent.trim().toLowerCase()

      if (checked) {
        current = input
      }

      if (text.startsWith(key)) {
        first ||= input

        if (current && current !== input) {
          next = input
          break
        }
      }
    }

    const input = next ?? first ?? current

    if (input) {
      preventDefault(event)
      clearTimeout(timeout)
      await select(list, input)
      timeout = setTimeout(cleanup, 1000)
    }
  }
})

const onListTapUp = object(tapDown, {
  capture,
  passive,
  task: async (list, event) => {
    const { target } = event
    const item = closest('li', target)

    if (!item) {
      return
    }

    const input = one('input', item)

    stopImmediatePropagation(event)

    if (opener(list).value !== input.value) {
      picked = true
      await select(list, input)
    }

    await wait(150)
    close()
  }
})

export default template => {
  const { parentNode } = template
  const { value } = parentNode

  templates.set(parentNode, template)
  values.set(parentNode, value)
  onSelectKeyUp.listen(parentNode)
  onSelectTapDown.listen(parentNode)
  onSelectTapUp.listen(parentNode)
}
